/**
 * Shared date-validation helpers.
 *
 * Three strategies exist in the codebase (see internal analysis 2026-07-21):
 * 1. Midnight‑normalized `Date` comparisons — for date‑only fields
 *    (dischargeDate, birthDate).
 * 2. Raw `new Date()` comparisons — for datetime fields (scheduledAt,
 *    callDateTime).
 * 3. String lexicographic comparison on `YYYY-MM-DD` — for cross‑field
 *    relativeness (close‑patient endDate vs dischargeDate).
 *
 * This module addresses strategy 1 (used in patient‑registration.schema.ts).
 * Strategies 2 & 3 remain inline where they serve distinct timing/domain needs.
 */

// ── Midnight helpers (date‑only comparisons) ──

/**
 * Return a `Date` set to today's midnight (00:00:00.000) in the local timezone.
 * Used as the cutoff for date‑only "must not be in the future" checks.
 */
export function todayMidnight(): Date {
	const d = new Date();
	d.setHours(0, 0, 0, 0);
	return d;
}

/**
 * Safely parse a date string.  Returns `null` on invalid input so the caller
 * can decide whether to issue a `validation:required` or skip silently.
 *
 * A date-only string (`YYYY-MM-DD`) is parsed at **local** midnight. `new Date("YYYY-MM-DD")`
 * is spec'd to parse as **UTC** midnight, which in KST (UTC+9) reads as 09:00 local — so a
 * date-only value compared against {@link todayMidnight} (local) makes TODAY look like the
 * future and wrongly rejects it (SHCT-531). Appending `T00:00:00` forces local parsing so the
 * two sides are comparable. Datetime strings (with a time part) are left untouched.
 */
export function parseDateSafe(raw: string): Date | null {
	if (!raw || raw.trim() === '') return null;
	const s = raw.trim();
	const local = /^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T00:00:00` : s;
	const d = new Date(local);
	return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * True when `raw` represents a date that is **today or earlier** (i.e. NOT in
 * the future).  Both `dischargeDate` and `birthDate` use this rule.
 */
export function isNotFutureDate(raw: string): boolean {
	const d = parseDateSafe(raw);
	if (!d) return false; // invalid / empty — let caller decide error
	return d <= todayMidnight();
}
